import { Alert, Typography } from '@mui/material';
import { useGridApiRef } from '@mui/x-data-grid-premium';
import { type ReactNode, useCallback, useContext, useMemo } from 'react';
import { useSearchParams } from 'react-router';

import type { PreviewList } from '~/analytics/events';
import { UserContext } from '~/auth/userContext';
import { ContractPreviewPanel } from '~/components/contractReview/ContractPreviewPanel';
import { getContractsTableColumns } from '~/components/contractReview/contractTableColumns';
import { mapContract } from '~/components/contractReview/mapContract';
import type { ContractReview } from '~/components/contractReview/types';
import {
  type CoveringContract,
  selectCoveringContracts,
} from '~/components/invoices/detailPage/related/coveringContracts';
import { useTableRowPreview } from '~/components/rowPreview/useTableRowPreview';
import { LiftAITable } from '~/components/table';
import type { LiftAIGridColDef } from '~/components/table/colDefTemplate';
import { useContractsForProperty } from '~/data/hooks/useContracts';
import type { Invoice } from '~/data/schemas/invoice';
import { useDetailTableHeight } from '~/hooks/useDetailTableHeight';
import { useLocaleTerms } from '~/hooks/useLocaleTerms';
import { buildUrlForContract } from '~/utils/urlBuilder';

/** A covering contract in the Contracts page's view model, plus what it covers. */
type CoveringContractRow = ContractReview & {
  coveredCarNames: string[];
};

/**
 * The contracts that govern this invoice. Two relationships, easy to confuse:
 * `invoice.activeContractId` is the contract the backend resolved as governing *this*
 * invoice (what the AI review priced against), while a contract covering one of the
 * serviced elevators may be relevant without having applied. `selectCoveringContracts`
 * sorts the governing one first.
 *
 * Rendered with the Contracts page's own column set, so a row reads the same here as it
 * does there; clicking one opens the contract's page in a new tab.
 */
export function ContractsSection({ invoice }: { invoice: Invoice }) {
  const propertyId = invoice.property?.id;
  const { contracts, isLoading } = useContractsForProperty(propertyId);
  const terms = useLocaleTerms();
  const covering = useMemo(() => selectCoveringContracts(contracts, invoice), [contracts, invoice]);

  if (!propertyId) {
    return <Alert severity="info">This invoice has no property, so no contract applies.</Alert>;
  }

  return (
    <CoveringContractsTable
      covering={covering}
      isLoading={isLoading}
      noCoverageLabel="None on this invoice"
      previewList="invoice_contracts"
      emptyMessage={`No contract covers this invoice or its serviced ${terms.vehicles.toLowerCase()}.`}
    />
  );
}

export function CoveringContractsTable({
  covering,
  isLoading,
  noCoverageLabel,
  emptyMessage,
  previewList,
}: {
  covering: CoveringContract[];
  isLoading: boolean;
  noCoverageLabel: string;
  emptyMessage: ReactNode;
  previewList: PreviewList;
}) {
  const tableHeight = useDetailTableHeight();
  const terms = useLocaleTerms();
  const { currencyCode } = useContext(UserContext);
  const apiRef = useGridApiRef();
  const preview = useTableRowPreview({ list: previewList, apiRef });
  const [searchParams] = useSearchParams();

  const rows = useMemo<CoveringContractRow[]>(
    () =>
      covering.map(({ contract, coveredCarNames }) => ({
        ...mapContract(contract),
        coveredCarNames,
      })),
    [covering],
  );
  const columns = useMemo<LiftAIGridColDef<CoveringContractRow>[]>(() => {
    const covered: LiftAIGridColDef<CoveringContractRow> = {
      field: 'coveredCarNames',
      headerName: `${terms.vehicles} covered`,
      flex: 1,
      minWidth: 180,
      filterable: false,
      sortable: false,
      valueGetter: (_, row) => row.coveredCarNames.join(', '),
      renderCell: ({ row }) =>
        row.coveredCarNames.length > 0 ? (
          <Typography variant="body2" noWrap>
            {row.coveredCarNames.join(', ')}
          </Typography>
        ) : (
          <Typography variant="body2" color="text.secondary">
            {noCoverageLabel}
          </Typography>
        ),
    };

    const base = getContractsTableColumns<CoveringContractRow>({
      currencyCode,
      vehiclesLabel: terms.vehicles,
    })
      // The property is fixed — this invoice's — so the properties roll-up says nothing, and
      // the contract-wide car count is the wrong number next to the elevators it covers here.
      .filter(
        (column) =>
          column.field !== 'properties' && column.field !== 'carCount' && column.field !== 'type',
      );

    // Sits next to the vendor rather than at the end: which elevators the contract covers
    // is the reason this section exists, and the pane is too narrow to trust a trailing
    // column. Falls back to the end of the row, since a `-1` from `findIndex` would
    // otherwise splice at 0 and lead the table with it.
    const vendorIndex = base.findIndex((column) => column.field === 'vendor');
    base.splice(vendorIndex === -1 ? base.length : vendorIndex + 1, 0, covered);
    return base;
  }, [terms, currencyCode, noCoverageLabel]);

  // A new tab rather than a navigation: the contract is opened to check what the invoice
  // was priced against, so the invoice has to still be there to check.
  const rowHref = useCallback(
    (row: CoveringContractRow) => buildUrlForContract(String(row.id), searchParams),
    [searchParams],
  );

  // An empty grid would say "No rows" where the reason matters, so the two ways
  // of having nothing to show stay as messages rather than an empty table.
  if (!isLoading && rows.length === 0) {
    return <Alert severity="info">{emptyMessage}</Alert>;
  }

  return (
    <>
      <LiftAITable<CoveringContractRow>
        height={tableHeight}
        apiRef={apiRef}
        rows={rows}
        columns={columns}
        rowCount={rows.length}
        loading={isLoading}
        tableHeaderTitle="Governing contract"
        disableRowSelectionOnClick
        rowHref={rowHref}
        getRowId={(row) => row.id}
        toolbarProps={{ filterControls: preview.peekSwitch }}
        {...preview.tableProps}
      />
      {preview.enabled ? <ContractPreviewPanel rowPreview={preview.rowPreview} /> : null}
    </>
  );
}
